import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Code,
  Key,
  Lock,
  Zap,
  CheckCircle,
  Copy,
  ExternalLink,
  ArrowRight,
  FileJson,
  Globe,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const APIPage = () => {
  const endpoints = [
    { method: 'GET', path: '/api/posts', description: 'List all posts' },
    { method: 'POST', path: '/api/posts', description: 'Create a new post' },
    { method: 'GET', path: '/api/posts/{id}', description: 'Get post by ID' },
    { method: 'PUT', path: '/api/posts/{id}', description: 'Update a post' },
    { method: 'DELETE', path: '/api/posts/{id}', description: 'Delete a post' },
    { method: 'POST', path: '/api/posts/{id}/publish', description: 'Publish a post' },
    { method: 'GET', path: '/api/calendars', description: 'List calendars' },
    { method: 'GET', path: '/api/analytics/overview', description: 'Get analytics overview' },
    { method: 'GET', path: '/api/media', description: 'List media files' },
    { method: 'POST', path: '/api/media/upload', description: 'Upload media' }
  ];

  const codeExample = `// Install the SDK
npm install @stellisocial/sdk

// Initialize the client
import { StelliSocial } from '@stellisocial/sdk';

const client = new StelliSocial({
  apiKey: 'sk_live_••••••••••••••••'  // Your API key from Settings > API
});

// Create a post
const post = await client.posts.create({
  content: 'Hello from the API!',
  platforms: ['twitter', 'linkedin'],
  scheduled_time: '2026-03-15T10:00:00Z'
});

// Get analytics
const analytics = await client.analytics.overview({
  start_date: '2026-03-01',
  end_date: '2026-03-31'
});`;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/integrations" className="text-slate-600 hover:text-slate-900">Integrations</Link>
              <Link to="/developers" className="text-emerald-600 font-medium">API</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-emerald-500/20 text-emerald-400 mb-4">API</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Build with the
            <span className="text-emerald-400"> StelliSocial API</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            RESTful API with comprehensive documentation to integrate social media management into your applications.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                Get API Key <Key className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-700">
              View Full Docs <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Zap, title: 'Fast & Reliable', description: '99.9% uptime SLA with global edge distribution for low latency.' },
              { icon: Shield, title: 'Secure', description: 'OAuth 2.0 authentication, rate limiting, and encrypted data transfer.' },
              { icon: FileJson, title: 'RESTful JSON', description: 'Clean, predictable REST endpoints with JSON request/response.' }
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="text-center border-slate-100">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Code Example */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Quick Start</h2>
              <p className="text-slate-600 mb-6">
                Get started in minutes with our official SDK or make direct HTTP requests.
              </p>
              <div className="bg-slate-900 rounded-xl p-6 overflow-auto">
                <pre className="text-sm text-slate-300">
                  <code>{codeExample}</code>
                </pre>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Available Endpoints</h2>
              <p className="text-slate-600 mb-6">
                Our API covers all aspects of social media management.
              </p>
              <div className="space-y-2">
                {endpoints.map((endpoint, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Badge className={`font-mono text-xs ${
                      endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                      endpoint.method === 'POST' ? 'bg-emerald-100 text-emerald-700' :
                      endpoint.method === 'PUT' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {endpoint.method}
                    </Badge>
                    <code className="text-sm text-slate-700 flex-1">{endpoint.path}</code>
                    <span className="text-sm text-slate-500 hidden sm:block">{endpoint.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">Rate Limits</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { plan: 'Starter', limit: '100 requests/min', color: 'emerald' },
              { plan: 'Professional', limit: '500 requests/min', color: 'blue' },
              { plan: 'Enterprise', limit: 'Unlimited', color: 'purple' }
            ].map((tier, i) => (
              <Card key={tier.plan} className="text-center border-slate-200">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-slate-900">{tier.plan}</h3>
                  <p className={`text-2xl font-bold mt-2 text-${tier.color}-600`}>{tier.limit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Build?</h2>
          <p className="text-emerald-100 mb-8">Start integrating StelliSocial into your application today.</p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
              Get Your API Key <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default APIPage;
